import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Clock, Loader, CreditCard } from 'lucide-react';
import { bookingService } from '../../services/api';
import toast from 'react-hot-toast';

interface Booking {
  id: number;
  code_reservation: string;
  date_debut: string;
  date_fin: string;
  nb_personnes: number;
  nb_nuits: number;
  montant_total: number;
  status: string;
  payment_status: string;
  apartment?: {
    id: number;
    titre: string;
    ville: string;
    adresse: string;
  };
  created_at: string;
}

const Bookings: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await bookingService.getMyBookings();
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement des réservations');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => new Intl.NumberFormat('fr-FR').format(price);
  const formatDate = (date: string) => new Date(date).toLocaleDateString('fr-FR', { 
    day: 'numeric', month: 'short', year: 'numeric' 
  });

  const getStatusBadge = (booking: Booking) => {
    // Cas spécial: paiement espèces en attente
    if (booking.status === 'pending' && booking.payment_status === 'pending') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">💵 Espèces - En attente</span>;
    }
    const badges: Record<string, { label: string; className: string }> = {
      pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-800' },
      confirmed: { label: 'Confirmée', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Annulée', className: 'bg-red-100 text-red-800' },
      completed: { label: 'Terminée', className: 'bg-neutral-100 text-neutral-800' },
    };
    const badge = badges[booking.status] || { label: booking.status, className: 'bg-neutral-100 text-neutral-800' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.className}`}>{badge.label}</span>;
  };

  const filteredBookings = bookings.filter(b => {
    if (filter === 'all') return true;
    return b.status === filter;
  });

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Mes réservations</h1>
          <p className="text-neutral-600">Gérez vos réservations</p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input w-auto"
        >
          <option value="all">Toutes ({bookings.length})</option>
          <option value="pending">En attente</option>
          <option value="confirmed">Confirmées</option>
          <option value="completed">Terminées</option>
          <option value="cancelled">Annulées</option>
        </select>
      </div>

      {filteredBookings.length === 0 ? (
        <div className="card p-8 text-center">
          <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-500 mb-4">
            {filter === 'all' ? 'Aucune réservation pour le moment.' : 'Aucune réservation avec ce statut.'}
          </p>
          <Link to="/" className="btn btn-primary">Explorer les appartements</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map((booking) => (
            <div key={booking.id} className="card p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono text-sm bg-neutral-100 px-2 py-1 rounded">
                      {booking.code_reservation}
                    </span>
                    {getStatusBadge(booking)}
                  </div>
                  
                  {booking.apartment && (
                    <h3 className="font-semibold text-lg mb-1">
                      <Link to={`/apartments/${booking.apartment.id}`} className="hover:text-primary-600">
                        {booking.apartment.titre}
                      </Link>
                    </h3>
                  )}
                  
                  <div className="flex flex-wrap gap-4 text-sm text-neutral-600 mt-2">
                    {booking.apartment && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {booking.apartment.ville}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(booking.date_debut)} → {formatDate(booking.date_fin)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {booking.nb_nuits} nuit{booking.nb_nuits > 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {booking.nb_personnes} pers.
                    </span>
                  </div>
                </div>
                
                <div className="flex flex-col items-end gap-2">
                  <div className="text-xl font-bold text-primary-600">
                    {formatPrice(booking.montant_total)} FCFA
                  </div>
                  
                  {booking.status === 'pending' && (
                    <Link 
                      to={`/locataire/bookings/${booking.id}`}
                      className="btn btn-primary btn-sm"
                    >
                      <CreditCard className="w-4 h-4 mr-1" />
                      Payer
                    </Link>
                  )}
                  
                  {booking.status === 'confirmed' && (
                    <span className="text-sm text-green-600">✓ Payée</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Bookings;
